import { motion, AnimatePresence } from 'framer-motion';
import { X, Lock, Plus } from 'lucide-react';
import { useMoodboards } from '@/hooks/use-moodboards';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import type { SaveToMoodboardPayload } from '@/types/moodboards';
import { supabase } from '@/integrations/supabase/client';
import { useEffect, useState } from 'react';

interface MoodboardDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  item: SaveToMoodboardPayload;
  onSelect: (moodboardId: string) => void;
}

export function MoodboardDrawer({ isOpen, onClose, item, onSelect }: MoodboardDrawerProps) {
  const [userId, setUserId] = useState<string | undefined>();
  const { data: moodboards = [] } = useMoodboards(userId);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setUserId(user?.id);
    });
  }, []);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[10001] bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Drawer */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed z-[10002] bottom-0 left-0 right-0 bg-[#0A0A0B] rounded-t-3xl shadow-2xl max-h-[85vh] flex flex-col border-t border-white/10"
          >
            {/* Handle */}
            <div className="flex items-center justify-center py-3">
              <div className="w-12 h-1.5 bg-white/20 rounded-full" />
            </div>

            {/* Header */}
            <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Save to Moodboard</h2>
              <button
                onClick={onClose}
                className="p-2 rounded-full hover:bg-white/10 transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Preview */}
            {item.image_url && (
              <div className="px-6 py-4 border-b border-white/10">
                <div className="flex gap-3 items-center">
                  <img
                    src={item.image_url}
                    alt="Preview"
                    className="w-16 h-16 rounded-lg object-cover ring-1 ring-white/10"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate text-white">
                      {item.caption || 'Untitled'}
                    </p>
                    {item.author_username && (
                      <p className="text-xs text-gray-400">
                        by @{item.author_username}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Moodboards List */}
            <ScrollArea className="flex-1 px-6 py-4">
              <div className="space-y-3">
                {moodboards.length === 0 ? (
                  <div className="py-12 text-center">
                    <p className="text-gray-400 mb-4">
                      No moodboards yet
                    </p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={onClose}
                      className="bg-white/5 border-white/10 hover:bg-white/10 text-white"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Create your first moodboard
                    </Button>
                  </div>
                ) : (
                  moodboards.map((moodboard) => (
                    <motion.button
                      key={moodboard.id}
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => onSelect(moodboard.id)}
                      className="w-full flex items-center gap-4 p-4 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 transition-all text-left group"
                    >
                      {/* Icon */}
                      <div className="text-3xl flex-shrink-0">{moodboard.icon}</div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold truncate text-white group-hover:text-primary transition-colors">{moodboard.name}</p>
                          {moodboard.is_private && (
                            <Lock className="w-3.5 h-3.5 text-gray-500" />
                          )}
                        </div>
                        <p className="text-sm text-gray-400">
                          {moodboard.item_count || 0} items
                        </p>
                      </div>

                      {/* Cover Image */}
                      {moodboard.cover_image_url && (
                        <img
                          src={moodboard.cover_image_url}
                          alt=""
                          className="w-14 h-14 rounded-lg object-cover ring-1 ring-white/10"
                        />
                      )}
                    </motion.button>
                  ))
                )}
              </div>
            </ScrollArea>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
